// ===== ICMS-ST de UM item =====
//
// Spec:  docs/superpowers/specs/2026-10-06-icms-st-na-proposta.md
// Regras: src/icms-st-regras.js (gerado a partir da parametrização ATIVA do Nomus)
//
// PRINCÍPIO (Kaio, 06/10 e 07/10): "mesmo que esteja errado, eu preciso que seja mantido o cálculo do Nomus até que seja
// corrigido, não posso passar um valor para o cliente e depois cobrar outro". O orçamento tem de bater com a nota que o
// Nomus emite. Onde o Nomus diverge da lei ou da contabilidade, o valor segue o Nomus e a regra leva um `alerta`.
//
// POR QUE ISTO EXISTE: a PDLED não pode enviar orçamento sem imposto, e a API do Nomus não expõe a tabela de tributação. A
// fonte é a parametrização ativa dele, conferida contra as NF-e que a PDLED realmente emitiu desde 01/01/2025 (2.348 de 2.378
// itens iguais ao centavo; os outros 30 em 1 centavo). É ESTIMATIVA: quem calcula e emite é o Nomus.
//
// A CONTA (por item), com o frete/seguro/despesas já rateados em `acessorios`:
//
//   base   = valor CHEIO do item + acessórios          <- sem subtrair desconto, sem IPI
//   BC_ST  = base × (1 + MVA)
//   ICMS-ST = BC_ST × alíquota_ST − base × dedução     (nunca negativo)
//   FCP-ST  = BC_ST × FCP                              <- separado, nunca somado à alíquota
//
// Fonte oficial da fórmula -- LC 87/96, art. 8º, II e § 5º (https://www.planalto.gov.br/ccivil_03/leis/lcp/lcp87.htm):
//   "o imposto a ser pago por substituição tributária […] corresponderá à diferença entre o valor resultante da aplicação da
//    alíquota prevista para as operações ou prestações internas do Estado de destino sobre a respectiva base de cálculo e o
//    valor do imposto devido pela operação ou prestação própria do substituto."
//
// O DESCONTO NÃO ENTRA NA BASE de propósito: o Nomus emite sem tirá-lo (448 de 448 itens com desconto desde 2025, medido). A
// contabilidade respondeu que o desconto sai da base; a divergência está com o financeiro.
//
// ARREDONDAMENTO: empate para o PAR (o Nomus parece usar `HALF_EVEN`), em cada etapa. Medido contra as notas: reproduz 2.348
// itens ao centavo; meio centavo para cima, 2.322. Os 30 restantes divergem 1 centavo (não decifrei o porquê).
//
// TIPO DE VENDA: o Nomus só aplica as regras de ST em VENDA LOJISTA, SHOWROOM e afins (`regra.movimentacoes`, IDs de tipo de
// movimentação). Em VENDA CORPORATIVA ou CONSUMIDOR FINAL ele NÃO cobra ST: 0 de 130 itens corporativos e 0 de 98 de consumidor
// final em 35 dias de notas. Sem olhar o tipo de venda, o orçamento mostraria um ST que a nota não traria.
//
// SEM REGRA: o Nomus não cobra ST onde não há regra ativa para UF + NCM + origem (156 de 156 itens de venda lojista a
// contribuinte nessa situação, 35 dias). O orçamento diz "sem ST", com o motivo.
//
// RJ (operação INTERNA): a dedução não é a interestadual da lei, é a que a regra do Nomus parametriza (`regra.deducao`).
// Fonte do FECP (secundária; o texto primário da SEFAZ-RJ não foi obtido): Alterdata, "Cálculo do FECP-ST no RJ"
// (https://ajuda.alterdata.com.br/fiscalbase/pack-fiscal-calculo-fecp-st-rj-57379211.html), que cita a Lei 4.056/02: em operação
// interna o FECP é 2% "sobre a diferença entre o valor da base de cálculo de retenção do imposto e o valor da base de cálculo da
// operação própria". Com ICMS de 20%, ICMS-ST + FCP-ST = 22% × (BC_ST − base) -- o que o Nomus cobra nas luminárias e o que a
// contabilidade confirmou em 02/10 ("22% é a alíquota efetiva"). Nas fontes e presilhas o Nomus cobra 22% + 2%: segue o Nomus.

import { dinheiro } from './dinheiro.js';

/** Onde as duas emissoras ficam (Av. das Américas, Rio de Janeiro). Operação interna não é interestadual. */
export const UF_ORIGEM = 'RJ';

// A Constituição divide o país em dois blocos para a alíquota interestadual: 12% entre Sul e Sudeste, 7% do Sul/Sudeste para
// Norte, Nordeste, Centro-Oeste e Espírito Santo. O ES é Sudeste mas cai no bloco de 7%.
const UF_12 = new Set(['MG', 'PR', 'RS', 'SC', 'SP']);
const UF_7 = new Set(['AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS',
                      'PA', 'PB', 'PE', 'PI', 'RN', 'RO', 'RR', 'SE', 'TO']);

// Resolução do Senado 13/2012, art. 1º e § 1º
// (https://www.planalto.gov.br/ccivil_03/_ato2011-2014/2012/Congresso/RSF-13-2012.htm):
//   "a alíquota […] nas operações interestaduais com bens e mercadorias importados do exterior, será de 4% (quatro por cento)",
//   para o que, "após seu desembaraço aduaneiro", "não tenham sido submetidos a processo de industrialização" ou "resultem em
//   mercadorias ou bens com Conteúdo de Importação superior a 40%". A contabilidade confirmou (02/10): "Ambos".
// ARMADILHA, no § 4º, I: "O disposto nos §§ 1º e 2º não se aplica: I - aos bens e mercadorias importados do exterior que não
// tenham similar nacional" -- são as origens 6 e 7, que ficam FORA dos 4%. A versão anterior deste arquivo as incluía.
const ORIGENS_4_POR_CENTO = new Set(['1', '2', '3', '8']);

const texto = (v) => (v == null ? '' : String(v).trim());

/** Sem acento, maiúsculas, espaços únicos: como o gerador normaliza os nomes de `regra.clientes`. */
export const normalizarNome = (nome) => texto(nome).normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase().split(/\s+/).filter(Boolean).join(' ');

/**
 * Alíquota do ICMS da operação própria que se deduz do ST (Simples Nacional, operação interestadual).
 *
 * Convênio ICMS 142/18, cláusula 13, § 1º (https://www.confaz.fazenda.gov.br/legislacao/convenios/2018/CV142_18):
 * "deverá ser deduzido, a título de ICMS da operação própria, o resultado da aplicação da alíquota interestadual estabelecida
 * pelo Senado Federal".
 *
 * Devolve `null` quando não sabe -- e quem chama trata como "a confirmar", NUNCA como zero (zero inflaria o ST) e NUNCA assume
 * origem nacional quando ela falta. No RJ a operação é interna: a dedução vem de `regra.deducao` (ver calcularItemST).
 */
export function deducaoInterestadual(ufDestino, origem) {
  const uf = texto(ufDestino).toUpperCase();
  const o = texto(origem);
  if (!uf || !o || uf === UF_ORIGEM) return null;
  if (ORIGENS_4_POR_CENTO.has(o)) return 4;
  if (UF_12.has(uf)) return 12;
  if (UF_7.has(uf)) return 7;
  return null;
}

// Índice por "UF|NCM|origem" -> lista de regras (a geral e as de clientes específicos). WeakMap: a lista é um array estático
// (ou, nos testes, um literal) e o índice some junto com ela.
const indices = new WeakMap();
const chave = (uf, ncm, origem) => `${texto(uf).toUpperCase()}|${texto(ncm)}|${texto(origem)}`;

/**
 * A regra da combinação exata UF + NCM + origem, ou `null`.
 *
 * `cliente` (nome ou lista de nomes: fantasia e razão social): o Nomus trata alguns clientes com regra PRÓPRIA (campo Pessoa de
 * uma regra; hoje a Casarão Lustres, MVA 20% no RJ). Se o nome bate com `regra.clientes`, vale a regra deles; para os demais,
 * a geral.
 */
export function resolverRegra(regras, { uf, ncm, origem, cliente } = {}) {
  if (!Array.isArray(regras) || !texto(uf) || !texto(ncm) || !texto(origem)) return null;
  let idx = indices.get(regras);
  if (!idx) {
    idx = new Map();
    for (const r of regras) {
      const k = chave(r.uf, r.ncm, r.origem);
      idx.set(k, [...(idx.get(k) || []), r]);
    }
    indices.set(regras, idx);
  }
  const candidatas = idx.get(chave(uf, ncm, origem)) || [];
  const nomes = (Array.isArray(cliente) ? cliente : [cliente]).map(normalizarNome).filter(Boolean);
  const propria = candidatas.find((r) => r.clientes && nomes.some((n) => r.clientes.includes(n)));
  return propria || candidatas.find((r) => !r.clientes) || null;
}

// Arredonda ao centavo com empate para o PAR, deslocando a vírgula pelo TEXTO (como dinheiro.js: multiplicar por 100 come
// centavo em ponto flutuante).
function par(valor) {
  const n = Number(valor);
  if (!Number.isFinite(n) || n === 0) return 0;
  const sinal = n < 0 ? -1 : 1;
  const x = Number(`${Math.abs(n)}e2`);
  if (!Number.isFinite(x)) return sinal * (Math.round(Math.abs(n) * 100) / 100);
  const piso = Math.floor(x);
  const centavos = Math.abs(x - piso - 0.5) < 1e-9 ? (piso % 2 === 0 ? piso : piso + 1) : Math.round(x);
  return sinal * Number(`${centavos}e-2`);
}

// Motivo do `sem_st` por tipo de venda. Exportado para a tela reconhecer o caso pelo CODIGO e nao por pedaco de texto:
// se alguem reescrever a frase, o aviso nao some em silencio.
export const MOTIVO_TIPO_VENDA_SEM_ST = 'o Nomus só cobra ST em venda Lojista ou Showroom: neste tipo de venda a nota sai sem ST';

/**
 * ICMS-ST de um item.
 *
 * `{ status: 'calculado', baseProp, baseST, deducaoPct, icmsSt, fcpSt, valor }` quando dá para calcular (`valor` = ICMS-ST +
 * FCP-ST, o que o cliente paga); `{ status: 'sem_st', motivo }` quando o Nomus NÃO cobra ST (sem regra para o NCM/estado, ou
 * tipo de venda sem ST); ou `{ status: 'a_confirmar', motivo }`.
 *
 * Aceita, e ignora de propósito, qualquer `desconto`: ver o cabeçalho.
 */
export function calcularItemST({ valorProduto, acessorios = 0, regra, ufDestino, origemProduto, tipoVendaId }) {
  if (!regra) return { status: 'sem_st', motivo: 'o Nomus não tem regra de ST para este NCM neste estado: a nota sai sem ST' };

  // O Nomus só aplica a regra no tipo de venda dela (VENDA LOJISTA, SHOWROOM...). Regra sem `movimentacoes` (literal de teste)
  // não restringe.
  if (Array.isArray(regra.movimentacoes) && regra.movimentacoes.length) {
    if (tipoVendaId == null || tipoVendaId === '') return { status: 'a_confirmar', motivo: 'o tipo de venda não foi informado, e o Nomus só cobra ST em venda Lojista/Showroom' };
    if (!regra.movimentacoes.includes(Number(tipoVendaId))) {
      return { status: 'sem_st', motivo: MOTIVO_TIPO_VENDA_SEM_ST };
    }
  }

  if (regra.nivel === 'a_confirmar') return { status: 'a_confirmar', motivo: regra.motivo || 'regra ainda não validada' };

  // RJ (operação interna) e onde o Nomus deduz algo diferente da lei: a dedução vem da regra. Nos demais, a da lei.
  const ded = regra.deducao != null ? Number(regra.deducao) : deducaoInterestadual(ufDestino, origemProduto);
  if (ded == null) return { status: 'a_confirmar', motivo: 'origem do produto ou estado de destino indefinidos' };

  const baseProp = dinheiro((Number(valorProduto) || 0) + (Number(acessorios) || 0));
  if (!(baseProp > 0)) return { status: 'a_confirmar', motivo: 'valor do item ausente' };

  const baseST = par(baseProp * (1 + Number(regra.mva) / 100));
  const icmsSt = Math.max(0, par(par(baseST * Number(regra.aliquota) / 100) - par(baseProp * ded / 100)));
  const fcpSt = par(baseST * (Number(regra.fcp) || 0) / 100);

  return { status: 'calculado', baseProp, baseST, deducaoPct: ded, icmsSt, fcpSt, valor: dinheiro(icmsSt + fcpSt) };
}
